import { ApolloClient } from '@apollo/client';
import axios from 'axios';
import { v4 as uuid } from 'uuid';

import { finleyAssert, finleyBuffer } from '@finley-technologies/common';
import { FinleyAi, typeguards } from '@finley-technologies/finley-types';
import type { TapeBuilderNodeAbstract } from '@finley-technologies/tape-builder';
import {
  Ai2CsvExtractor,
  baseFieldsToFinleyArrayJsonSchema,
} from '@finley-technologies/tape-sanitizer';

import {
  GetExtractAi2CsvQueryGQLType,
  GetExtractAi2CsvQueryVariablesGQLType,
} from '../../../../../types/dashboard-admin-gqlTypes.generated.d';
import { presignedPostUpload } from 'packages/files/ui/utils';
import { GET_AI2CSV_UPLOAD_SIGNED_URL } from 'packages/tapeBuilder/requests/tapeBuilder.gql';
import { GetExtractAi2CsvDocumentGQLType } from 'packages/tapeBuilder/requests/tapeBuilder.gql.generated';
import { TapeBuilderBuffer } from 'packages/tapeBuilder/ui/calcAgentQA/QAContext';

export const getAi2CsvExtractor = ({
  apolloClient,
  pipelineDefinitionId,
  nodeId,
  getInputNodeIds,
  getNodeById,
  setQAStatus,
  getNodeBuffer,
  extensionMap,
}: {
  apolloClient: ApolloClient<object>;
  pipelineDefinitionId: string;
  nodeId: string;
  getInputNodeIds: (nodeId: string) => string[];
  extensionMap: Map<string, string>;
  getNodeById: (nodeId: string) => TapeBuilderNodeAbstract;
  setQAStatus: (status: string) => Promise<void>;
  getNodeBuffer: (nodeId: string) => TapeBuilderBuffer;
}): Ai2CsvExtractor => {
  return async ({
    extractionConfigs,
    baseFields,
    prompt,
    baseFieldsTemplate,
  }) => {
    await setQAStatus('Ai2Csv: Uploading files.');

    const tool = baseFieldsToFinleyArrayJsonSchema({
      fields: baseFields,
      baseFieldsTemplate,
    });

    const inputNodeIds = getInputNodeIds(nodeId);

    const documents: FinleyAi.TransformExtractionConfig[] = await Promise.all(
      inputNodeIds.map(async (inputNodeId) => {
        const node = getNodeById(inputNodeId);

        finleyAssert(
          'artifactSlug' in node.configuration &&
            typeof node.configuration.artifactSlug === 'string',
          `artifactSlug must be defined for [nodeId:${inputNodeId}`,
        );

        const documentName = node.configuration.artifactSlug;

        const fileExtension = extensionMap.get(inputNodeId);

        finleyAssert(
          typeguards.finleyAi.isFinleyAiTransformSupportedFileExtension(
            fileExtension,
          ),
          `getAi2CsvExtractor: unsupported file extension ${fileExtension}`,
        );

        const buffer = getNodeBuffer(inputNodeId);

        finleyAssert(
          buffer,
          `getAi2CsvExtractor: buffer must exist for [nodeId:${inputNodeId}]`,
        );

        // TypeScript 6.0: Use helper to get a properly typed ArrayBuffer
        const blob = new Blob([finleyBuffer.toArrayBuffer(buffer)], {
          type: fileExtension,
        });

        const file = new File([blob], `ai2Csv-${inputNodeId}`, {
          type: fileExtension,
        });

        // get presignedUrl and upload to s3
        const { queryResponse } = await presignedPostUpload<
          {
            pipelineDefinitionId: string;
            documentName: string;
            fileExtension: string;
            requestId: string;
          },
          { s3Bucket: string; s3Path: string }
        >({
          client: apolloClient,
          file,
          query: GET_AI2CSV_UPLOAD_SIGNED_URL,
          queryVariables: {
            pipelineDefinitionId,
            documentName,
            fileExtension,
            requestId: uuid(),
          },
        });

        const document: FinleyAi.TransformExtractionConfig = {
          documentName,
          s3Bucket: queryResponse.s3Bucket,
          s3Key: queryResponse.s3Path,
        };

        return document;
      }),
    );

    if (extractionConfigs) {
      extractionConfigs.forEach((config) => {
        const document = documents.find(
          (doc) => doc.documentName === config.artifactSlug,
        );
        finleyAssert(
          document,
          `createAi2CsvExtractor: unable to find [inputArtifactSlug:${document?.documentName}] for configured [artifactSlug:${config.artifactSlug}]`,
        );
        document.extraction = config.configuration;
      });
    }

    await setQAStatus('getAi2CsvExtractor: Starting extraction.');

    // long poll for csv completion signed download url
    // may need to revisit this requests are timing out
    const { data: extractedCsvSignedDownload } = await apolloClient.query<
      GetExtractAi2CsvQueryGQLType,
      GetExtractAi2CsvQueryVariablesGQLType
    >({
      query: GetExtractAi2CsvDocumentGQLType,
      variables: {
        pipelineDefinitionId,
        prompt,
        tool,
        documentsStr: JSON.stringify(documents),
        baseFieldsTemplate,
      },
    });

    await setQAStatus('getAi2CsvExtractor: Downloading extracted csv.');

    // download the extract csv to client memory
    const res = await axios({
      url: extractedCsvSignedDownload.extractAi2Csv,
      method: 'GET',
      responseType: 'arraybuffer',
    });

    const buffer = finleyBuffer.fromArrayBuffer(res.data);

    await setQAStatus('');

    return {
      isValid: true,
      data: { outputBuffer: buffer },
      supplementals: [],
    };
  };
};
